import { memo, useCallback } from 'react';
import styled from 'styled-components';
import { isFunction } from '@agilepoint/utils-common';
import { ICollapsibleMenuProps } from './collapsible-menu-types';
import { CollapsibleMenuItem } from './collapsible-menu-item';
import { TreeView, ITreeViewRenderItemProps } from '../../tree-view';
import { getCommonMenuStyle } from '../common';

const CollapsibleMenuBase = memo((props: ICollapsibleMenuProps) => {
    const {
        className, items, defaultMode, selectedId, cursorType, showSearchBox, defaultSelectedId, onRenderContent,
        searchPlaceholder, triggerChangeOnItemWithChildren, onChange, onModeChange, hideCollapsibleButton, onRenderItem,
        disableModeChangeOnHover, defaultExpandedId, enableAccordionMode, retainHierarchyInSearchResult, searchPredicate
    } = props;

    const _onChange = useCallback((id: string) => {
        if (onChange === undefined) {
            return;
        }
        onChange(id);
    }, [onChange]);

    const _onRenderItem = useCallback((renderProps: ITreeViewRenderItemProps) => (
        isFunction(onRenderItem) ? onRenderItem(renderProps) :
            <CollapsibleMenuItem
                {...renderProps}
                cursorType={cursorType}
                triggerChangeOnParentSelection={triggerChangeOnItemWithChildren}
                key={renderProps.item.id}
                onRenderContent={onRenderContent} />
    ), [cursorType, onRenderContent, onRenderItem, triggerChangeOnItemWithChildren]);

    return (
        <TreeView
            className={className}
            items={items}
            selectedId={selectedId}
            enableAccordionMode={enableAccordionMode}
            defaultSelectedId={defaultSelectedId}
            defaultMode={defaultMode}
            showSearchBox={showSearchBox}
            triggerChangeOnParentSelection={triggerChangeOnItemWithChildren}
            hideCollapsibleButton={hideCollapsibleButton}
            disableModeChangeOnHover={disableModeChangeOnHover}
            defaultExpandedId={defaultExpandedId}
            searchPlaceholder={searchPlaceholder}
            retainHierarchyInSearchResult={retainHierarchyInSearchResult}
            searchPredicate={searchPredicate}
            onChange={_onChange}
            onRenderItem={_onRenderItem}
            onRenderContent={onRenderContent}
            onModeChange={onModeChange} />
    );
});

CollapsibleMenuBase.displayName = 'CollapsibleMenuBase';

export const CollapsibleMenu = styled(CollapsibleMenuBase).attrs({ className: String('ap-collapsibleMenu') })`
    ${getCommonMenuStyle('collapsibleMenu')}
    overflow: visible;

    ${TreeView} {
        width: 100%;
        height: 100%;
    }
`;

CollapsibleMenu.displayName = 'CollapsibleMenu';
